import React, { useState, useMemo, useCallback } from "react";
import MUIDataTable from "mui-datatables";
import { CircularProgress, Backdrop, IconButton, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

const PaginationTable = ({
  title,
  columns,
  data,
  totalItems, // Renamed from totalRecords to reflect total records
  currentPage,
  rowsPerPage = 10,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  onSearchChange = () => {},
  onDownloadData = null,
  onFilterChange = () => {},
  filter = true,
  selectableRowsHideCheckboxes = true,
  expandableRows = false,
  onRowsSelect = () => {},
  name = "",
  hideDownloadButton = false,
  currentFilters = {}, // Accept current filters from parent
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleCustomDownload = useCallback(async () => {
    if (typeof onDownloadData != "function") return;
  
    setIsDownloading(true);
    try {
      const rawData = await onDownloadData(); // Fetch all records
      let csvRows;

      if(name == 'students'){
        const headers = [
          "ID",
          "First Name",
          "Last Name",
          "Email",
          "whatsapp_phone_number",
          "Course Name",
          "Progress",
          "Status",
        ];
    
        // Flatten data: one row per user per course
        const formattedData = rawData.flatMap((user) => {
          const courses = user.course_progress || [];
    
          if (courses.length === 0) {
            return [[
              user.id,
              user.first_name,
              user.last_name,
              user.email,
              user.whatsapp_phone_number,
              "", "", user.is_active === 1 ? "Active" : "Inactive",
            ]];
          }
    
          return courses.map((course) => [
            user.id,
            user.first_name,
            user.last_name,
            user.email,
            user.whatsapp_phone_number,
            course.course_name || "",
            course.progress ?? "",
            user.is_active === 1 ? "Active" : "Inactive",
          ]);
        });
        csvRows = [
          headers.map((h) => `"${h}"`).join(","), // Header
          ...formattedData.map((row) =>
            row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")
          ), 
        ];
      }else{
         // Exclude the last 3 columns
        const trimmedColumns = columns.slice(0, -3);

        const formattedData = rawData.map((row) =>
          trimmedColumns.map((col) => {
            const key = col.name || col.label || "";
            return row[key] ?? "";
          })
        );

        csvRows = [
          trimmedColumns.map((col) => `"${col.label}"`).join(","), // Header
          ...formattedData.map((row) =>
            row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")
          ), // Body
        ];
      }

  
      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
  
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "all_"+name+".csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setIsDownloading(false);
    }
  }, [onDownloadData, name, columns]);

  // Convert currentFilters object to filterList array format for MUIDataTable
  // filterList is an array where each index corresponds to a column
  const filterList = useMemo(() => {
    return columns.map((col) => {
      const columnName = col.name || col.label || "";
      if (columnName && currentFilters[columnName]) {
        // Return array of filter values
        return Array.isArray(currentFilters[columnName]) 
          ? currentFilters[columnName] 
          : [currentFilters[columnName]];
      }
      return [];
    });
  }, [columns, currentFilters]);

  // Memoize options to prevent unnecessary re-renders, but update when filterList changes
  const options = useMemo(() => ({
    filterType: "dropdown",
    filter: filter,
    print: false,
    viewColumns: false,
    search: true,
    selectableRowsHideCheckboxes: selectableRowsHideCheckboxes,
    selectableRows: "multiple",
    rowsPerPage: rowsPerPage,
    serverSide: true,
    rowsPerPageOptions: [10, 20, 50, 100],
    count: totalItems,
    page: currentPage - 1,
    onChangePage: (newPage) => onPageChange(newPage + 1),
    onChangeRowsPerPage: onRowsPerPageChange,
    expandableRows: expandableRows,
    onRowSelectionChange: onRowsSelect,
    onSearchChange: (searchText) => {
      const sanitized = searchText?.trim() ?? "";
      onSearchChange(sanitized);
    },
    onFilterChange: (changedColumn, filterList, type, columnIndex) => {
      // Helper function to clean filter values
      const cleanFilterValue = (value) => {
        if (!value) return value;
        if (Array.isArray(value)) {
          return value.map(v => {
            const str = String(v);
            // Remove query string fragments like ?v=1
            return str.split('?')[0].trim().replace(/%3F.*$/, '');
          });
        }
        const str = String(value);
        return str.split('?')[0].trim().replace(/%3F.*$/, '');
      };

      // Build filter object from filterList
      const filters = {};
      columns.forEach((col, idx) => {
        const filterValue = filterList[idx];
        if (filterValue && filterValue.length > 0) {
          const columnName = col.name || col.label || "";
          if (columnName) {
            filters[columnName] = cleanFilterValue(filterValue);
          }
        }
      });
      onFilterChange(filters);
    },
    filterList: filterList,
    download: false, // disable default download
    customToolbar: hideDownloadButton ? null : () => (
      <Tooltip title="Download All as CSV">
        <IconButton onClick={handleCustomDownload}>
          <DownloadIcon />
        </IconButton>
      </Tooltip>
    ),
  }), [filter, selectableRowsHideCheckboxes, rowsPerPage, totalItems, currentPage, expandableRows, onRowsSelect, onPageChange, onRowsPerPageChange, onSearchChange, onFilterChange, columns, filterList, hideDownloadButton, handleCustomDownload]);

  return (
    <>
      <MUIDataTable
        title={title}
        data={data}
        columns={columns}
        options={options}
      />
      <Backdrop open={isDownloading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
        <span style={{ marginLeft: 16 }}>Preparing CSV File...</span>
      </Backdrop>
    </>
  );
};

export default PaginationTable;
